import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  FlatList,
  Linking,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Button, Text, TextInput as PaperTextInput } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import JobsContext from '../context/JobsContext';
import { Application, ApplicationStatus } from '../types';
import { cleanInlineText } from '../utils';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import DatePicker from '../components/DatePicker';
import ModalSheet from '../components/ModalSheet';
import AdBanner from '../components/AdBanner';
import NativeAdCard from '../components/NativeAdCard';
import LoggedOutBanner from '../components/LoggedOutBanner';

type ApplicationRow =
  | { kind: 'app'; application: Application }
  | { kind: 'ad'; key: string };

const STATUSES: ApplicationStatus[] = ['applied', 'interviewing', 'offer', 'rejected'];

// Theme-token colour per status (no hardcoded hex — pulled from the palette).
const statusColor = (p: Palette, status: ApplicationStatus): string => {
  switch (status) {
    case 'interviewing':
      return p.secondary;
    case 'offer':
      return p.appliedText;
    case 'rejected':
      return p.danger;
    default:
      return p.textMuted;
  }
};

const ApplicationCard: React.FC<{ application: Application }> = ({ application }) => {
  const { t, i18n } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const {
    setApplicationStatus,
    setApplicationNotes,
    setApplicationStageDate,
    removeApplicationStage,
    removeApplication,
  } = useContext(JobsContext);

  const [dateEditStage, setDateEditStage] = useState<ApplicationStatus | null>(null);

  const formatDate = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(i18n.language || undefined);
  };

  const openUrl = () => {
    if (application.url) Linking.openURL(application.url).catch(() => {});
  };

  const remove = () => {
    const doRemove = () => {
      removeApplication(application.jobId);
      track('application_remove', { jobId: application.jobId });
    };
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || window.confirm(t('applications.removeConfirm'))) doRemove();
    } else {
      Alert.alert(t('applications.removeConfirm'), undefined, [
        { text: t('common.cancel') },
        { text: t('applications.remove'), style: 'destructive', onPress: doRemove },
      ]);
    }
  };

  const stageEntries = STATUSES.filter((s) => application.stageDates[s]);
  const editingDate =
    dateEditStage && application.stageDates[dateEditStage]
      ? new Date(application.stageDates[dateEditStage] as string)
      : new Date();

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <TouchableOpacity style={styles.cardTitleWrap} onPress={openUrl} disabled={!application.url}>
          <Text style={styles.position} numberOfLines={2}>
            {cleanInlineText(application.position) || application.jobId}
          </Text>
          {application.company ? (
            <Text style={styles.company} numberOfLines={1}>
              {cleanInlineText(application.company)}
            </Text>
          ) : null}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={remove}
          hitSlop={8}
          style={styles.removeBtn}
          accessibilityLabel={t('applications.remove')}
        >
          <MaterialCommunityIcons name="trash-can-outline" size={20} color={palette.danger} />
        </TouchableOpacity>
      </View>

      <View style={styles.statusRow}>
        {STATUSES.map((status) => {
          const active = application.status === status;
          const color = statusColor(palette, status);
          return (
            <TouchableOpacity
              key={status}
              onPress={() => {
                if (!active) {
                  setApplicationStatus(application.jobId, status);
                  track('application_status_change', { jobId: application.jobId, status });
                }
              }}
              style={[
                styles.statusChip,
                {
                  borderColor: active ? color : palette.border,
                  backgroundColor: active ? palette.surfaceMuted : 'transparent',
                },
              ]}
            >
              <Text style={[styles.statusText, { color: active ? color : palette.textMuted }]}>
                {t(`applications.status.${status}`)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {stageEntries.length > 0 && (
        <View style={styles.stages}>
          {stageEntries.map((s) => (
            <View key={s} style={styles.stageRow}>
              <View style={[styles.stageDot, { backgroundColor: statusColor(palette, s) }]} />
              <Text style={styles.stageLabel}>{t(`applications.status.${s}`)}</Text>
              <TouchableOpacity
                style={styles.stageDateBtn}
                onPress={() => setDateEditStage(s)}
                accessibilityLabel={t('applications.editDate')}
              >
                <Text style={styles.stageDate}>{formatDate(application.stageDates[s])}</Text>
                <MaterialCommunityIcons name="calendar-edit" size={16} color={palette.textMuted} />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => removeApplicationStage(application.jobId, s)}
                hitSlop={8}
                style={styles.stageRemove}
                accessibilityLabel={t('applications.remove')}
              >
                <MaterialCommunityIcons name="close" size={16} color={palette.textMuted} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      <TextInput
        style={styles.notes}
        value={application.notes}
        onChangeText={(text) => setApplicationNotes(application.jobId, text)}
        onBlur={() => application.notes && track('application_note_add', { jobId: application.jobId })}
        placeholder={t('applications.notesPlaceholder')}
        placeholderTextColor={palette.textMuted}
        multiline
        underlineColorAndroid="transparent"
      />

      {application.updatedAt ? (
        <Text style={styles.lastUpdate}>
          {t('applications.lastUpdate', { date: formatDate(application.updatedAt) })}
        </Text>
      ) : null}

      <DatePicker
        isVisible={dateEditStage !== null}
        onCancel={() => setDateEditStage(null)}
        onConfirm={(d) => {
          if (dateEditStage) setApplicationStageDate(application.jobId, dateEditStage, d.toJSON());
          setDateEditStage(null);
        }}
        initialDate={editingDate}
        maximumDate={new Date()}
      />
    </View>
  );
};

export default function ApplicationsScreen() {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { applications, addManualApplication } = useContext(JobsContext);

  const [addVisible, setAddVisible] = useState(false);
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');

  const closeAdd = () => {
    setAddVisible(false);
    setCompany('');
    setPosition('');
  };

  const submitAdd = () => {
    if (!position.trim()) return;
    addManualApplication(company, position);
    track('application_add_manual');
    closeAdd();
  };

  // Stable order, oldest → newest by appliedAt. Crucially this does NOT depend
  // on updatedAt, so editing notes/status/dates never reorders the list.
  const items = useMemo(
    () =>
      Object.values(applications).sort((a, b) => {
        const ta = Date.parse(a.appliedAt) || 0;
        const tb = Date.parse(b.appliedAt) || 0;
        if (ta !== tb) return ta - tb;
        return a.jobId < b.jobId ? -1 : a.jobId > b.jobId ? 1 : 0;
      }),
    [applications]
  );

  // Interleave a native ad after every 6th application.
  const rows = useMemo<ApplicationRow[]>(() => {
    const out: ApplicationRow[] = [];
    items.forEach((application, idx) => {
      out.push({ kind: 'app', application });
      if ((idx + 1) % 6 === 0) out.push({ kind: 'ad', key: `ad-${idx + 1}` });
    });
    return out;
  }, [items]);

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{t('applications.title')}</Text>
          <TouchableOpacity
            style={styles.addBtn}
            onPress={() => setAddVisible(true)}
            accessibilityLabel={t('applications.addManual')}
          >
            <MaterialCommunityIcons name="plus" size={18} color={palette.elevated} />
            <Text style={styles.addBtnText}>{t('applications.addManual')}</Text>
          </TouchableOpacity>
        </View>

        <LoggedOutBanner />

        {items.length === 0 ? (
          <View style={styles.empty}>
            <MaterialCommunityIcons name="clipboard-text-outline" size={44} color={palette.textMuted} />
            <Text style={styles.emptyTitle}>{t('applications.empty.title')}</Text>
            <Text style={styles.emptySubtitle}>{t('applications.empty.subtitle')}</Text>
          </View>
        ) : (
          <FlatList
            data={rows}
            keyExtractor={(item) => (item.kind === 'ad' ? item.key : item.application.jobId)}
            renderItem={({ item }) =>
              item.kind === 'ad' ? <NativeAdCard /> : <ApplicationCard application={item.application} />
            }
            style={styles.list}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
          />
        )}
        <AdBanner />
      </View>

      <ModalSheet visible={addVisible} onDismiss={closeAdd} cardStyle={styles.addCard}>
        <View style={styles.addBody}>
          <Text style={styles.addTitle}>{t('applications.addManual')}</Text>
          <PaperTextInput
            mode="outlined"
            label={t('resume.fields.company')}
            value={company}
            onChangeText={setCompany}
            style={styles.addInput}
          />
          <PaperTextInput
            mode="outlined"
            label={t('resume.fields.position')}
            value={position}
            onChangeText={setPosition}
            style={styles.addInput}
          />
          <View style={styles.addActions}>
            <Button mode="text" onPress={closeAdd} textColor={palette.textSecondary}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={submitAdd}
              disabled={!position.trim()}
              buttonColor={palette.primary}
              textColor="#0F1115"
            >
              {t('common.add')}
            </Button>
          </View>
        </View>
      </ModalSheet>
    </SafeAreaView>
  );
}

const isWeb = Platform.OS === 'web';

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: p.canvas },
    container: {
      flex: 1,
      width: '100%',
      maxWidth: isWeb ? 720 : undefined,
      alignSelf: isWeb ? 'center' : undefined,
      backgroundColor: p.canvas,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
    },
    headerTitle: { color: p.textPrimary, fontSize: 18, fontWeight: '700' },
    list: { flex: 1 },
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: p.primary,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderRadius: radius.pill,
    },
    addBtnText: { color: p.elevated, fontSize: 13, fontWeight: '700' },
    addCard: { backgroundColor: p.surface, borderRadius: radius.lg },
    addBody: { padding: spacing.lg, gap: spacing.sm },
    addTitle: { fontSize: 18, fontWeight: '700', color: p.textPrimary, marginBottom: spacing.xs },
    addInput: { backgroundColor: p.surface },
    addActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    listContent: { padding: spacing.lg, gap: spacing.md, paddingBottom: spacing.xxl },
    card: {
      backgroundColor: p.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: p.border,
      padding: spacing.lg,
      gap: spacing.md,
    },
    cardHeader: { flexDirection: 'row', alignItems: 'flex-start' },
    cardTitleWrap: { flex: 1, paddingRight: spacing.sm },
    position: { fontSize: 15, fontWeight: '700', color: p.textPrimary },
    company: { fontSize: 13, color: p.textSecondary, marginTop: 2 },
    removeBtn: { padding: spacing.xs },
    statusRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    statusChip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderRadius: radius.pill,
      borderWidth: 1,
    },
    statusText: { fontSize: 13, fontWeight: '600' },
    stages: {
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderRadius: radius.sm,
      backgroundColor: p.surfaceMuted,
    },
    stageRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    stageDot: { width: 8, height: 8, borderRadius: 999 },
    stageLabel: { flex: 1, fontSize: 15, color: p.textSecondary },
    stageDateBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 2 },
    stageDate: { fontSize: 15, color: p.textPrimary, fontWeight: '600' },
    stageRemove: { padding: spacing.xs },
    notes: {
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: radius.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      color: p.textPrimary,
      fontSize: 15,
      minHeight: 44,
      backgroundColor: p.surfaceMuted,
    },
    lastUpdate: { fontSize: 11, color: p.textSecondary },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl, gap: spacing.sm },
    emptyTitle: { fontSize: 16, fontWeight: '700', color: p.textPrimary, textAlign: 'center' },
    emptySubtitle: { fontSize: 14, color: p.textSecondary, textAlign: 'center' },
  });
